import { SnackbarProvider, useIsDarkMode } from '@chronosphereio/chrono-ui';
import {
  chronosphereLogsClient,
  FeatureFlagContextProvider,
  GatewayConfigsContextProvider,
} from '@chronosphereio/core';
import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Provider as UrqlProvider } from 'urql';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { ChronoAdminProvider } from '@/admin';
import { queryClient } from '@/clients/react-query-client';
import { urqlClient } from '@/clients/urql-client';
import { PageErrorBoundary } from '@/components';
import { ModalProvider } from '@/components/modal';
import { ActiveResourceProvider } from '@/context/ActiveResourceContext';
import { AppHeaderProvider } from '@/context/AppHeaderContext';
import { AnalyticsProvider } from '@/context/analytics/Analytics';
import { CloudAuthProvider } from '@/context/CloudAuthProvider';
import { CloudChartsProvider } from '@/context/CloudChartsProvider';
import { NavigationBlockerContextProvider } from '@/context/NavigationBlockerContext';
import { ThemeContextProvider } from '@/context/ThemeContextProvider';
import { UserTimePreferencesProvider } from '@/context/UserTimePreferencesProvider';
import { trackPageVisit, useForceRefreshAfterPeriod } from '@/utils';
import { TimeZoneProvider } from '@/viz-framework';
import { getChronosphereUiAnalytics } from './context/analytics/chronosphere-ui-analytics';
import { useFavicon } from './utils/document-title';

export const APPLICATION_ID = 'root';

const App = () => {
  const isDarkMode = useIsDarkMode();
  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);
  const chronosphereUiAnalytics = getChronosphereUiAnalytics();

  useFavicon();

  // Force refresh if the tab has been open for longer than 2 weeks
  useForceRefreshAfterPeriod();

  useEffect(() => {
    const rootElement = document.getElementById(APPLICATION_ID);

    if (rootElement) {
      rootElement.style.colorScheme = isDarkMode ? 'dark' : 'light';
    }
  }, [isDarkMode]);

  useEffect(() => {
    if (location.pathname === prevPathnameRef.current) {
      chronosphereUiAnalytics.trackPageView();
    } else {
      chronosphereLogsClient.page();
      trackPageVisit(prevPathnameRef.current, location.pathname);
      chronosphereUiAnalytics.trackPageView(prevPathnameRef.current);
      prevPathnameRef.current = location.pathname;
    }
  }, [location.pathname, chronosphereUiAnalytics]);

  return <Outlet />;
};

export const AppWrapper = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <UrqlProvider value={urqlClient}>
        <QueryParamProvider adapter={ReactRouter6Adapter} options={{ includeAllParams: true, params: {} }}>
          <CloudAuthProvider>
            <UserTimePreferencesProvider>
              <FeatureFlagContextProvider>
                <ThemeContextProvider applicationRoot={`#${APPLICATION_ID}`}>
                  <SnackbarProvider>
                    <GatewayConfigsContextProvider>
                      <AnalyticsProvider>
                        <ActiveResourceProvider>
                          <CloudChartsProvider>
                            <TimeZoneProvider>
                              <AppHeaderProvider>
                                <PageErrorBoundary>
                                  <ChronoAdminProvider>
                                    <ModalProvider>
                                      <NavigationBlockerContextProvider>
                                        <App />
                                      </NavigationBlockerContextProvider>
                                    </ModalProvider>
                                  </ChronoAdminProvider>
                                </PageErrorBoundary>
                              </AppHeaderProvider>
                            </TimeZoneProvider>
                          </CloudChartsProvider>
                        </ActiveResourceProvider>
                      </AnalyticsProvider>
                    </GatewayConfigsContextProvider>
                  </SnackbarProvider>
                </ThemeContextProvider>
              </FeatureFlagContextProvider>
            </UserTimePreferencesProvider>
          </CloudAuthProvider>
        </QueryParamProvider>
      </UrqlProvider>
    </QueryClientProvider>
  );
};
